import { Navigation, StringUtil, UrlUtil } from "@mcleod/core";
import { AutogenLayoutOrderTabView } from "./autogen/AutogenLayoutOrderTabView";
import {
    ContextMenuLabelProps, CrudDecorator, DataHeader, DataSource, DataSourceAction, DataSourceExecutionEvent,
    DataSourceMode, Layout, Snackbar, Tab
} from "@mcleod/components";
import { Orders } from "./Orders";
import { MovementTabView } from "./MovementTabView";
import { CommonDialogs } from "@mcleod/common";
import { Movement } from "./Movement";
import { InvoiceHistoryTabView } from "@mcleod/ar/src/InvoiceHistoryTabView";
import { StopList } from "./StopList";

interface OrderTabViewNavigateProps {
    key?: string;
    orderId?: string;
    newTab?: boolean;
    additionalProps?: {};
    styleProps?: {};
    doAfterNavigate?: (value: Window) => void;
}

enum OrderTabViewMode {
    ORDER = "order",
    MOVEMENT = "movement",
    HISTORY = "history"
}

const layoutURL = "lme/dispatch/OrderTabView";
export class OrderTabView extends AutogenLayoutOrderTabView {
    private _activeLayout: Orders | MovementTabView | InvoiceHistoryTabView;
    private _orderId: string;
    private mode: DataSourceMode;

    public static navigateTo(props: OrderTabViewNavigateProps): void {
        if ((props.key == null || props.key === "null") && props.orderId == null && props.additionalProps?.["create_spot"] == null) {
            Snackbar.showWarningSnackbar("No current movement associated with this driver")
            return;
        }
        let url = layoutURL + `?mode=${props.key ? DataSourceMode.UPDATE : DataSourceMode.ADD}`
            + (props.key ? `&key=${props.key}` : "")
            + (props.orderId ? `&orderId=${props.orderId}` : "");
        // When opening in a new tab, additionalProps aren't passed to the route, so add them as URL parameters
        if (props.newTab && props.additionalProps?.["showVoid"] === true) {
            url += "&showVoid=true";
        }
        Navigation.navigateTo(url, { newTab: props.newTab, ...props.styleProps }, { additionalProps: props.additionalProps }).then((value: Window) => {
            props.doAfterNavigate?.(value);
        });
    }

    public static slideUp(props: OrderTabViewNavigateProps): void {
        const url = `/lme/dispatch/OrderTabView${UrlUtil.buildQueryString({ mode: "update", key: props.key, ...(props.orderId ? { orderId: props.orderId } : {}) })}`;
        const layout = Layout.getLayout("lme/dispatch/OrderTabView") as OrderTabView;
        layout["additionalProps"] = props.additionalProps;
        const orderPanel = new CrudDecorator({
            layout: layout,
            mode: DataSourceMode.UPDATE,
            key: props.key,
            pseudoNavigateUrl: url,
            headerProps: {
                showClose: true,
                showSaveAndClose: true
            },
            doAfterSlideIn: (orderPanel) => {
                const orderTabView = orderPanel.layout as OrderTabView;
                orderTabView.addLayoutLoadListener(() => {
                    if (props.additionalProps?.["showVoid"] && orderTabView.layoutMovementTabView) {
                        orderTabView.layoutMovementTabView.showVoid = props.additionalProps?.["showVoid"];
                    }

                    // This additionalProps parameter allows specifying a tab on the Orders layout to scroll into view
                    if (props.additionalProps?.["selectedOrderTab"]) {
                        const tab = orderTabView.findComponentById(props.additionalProps?.["selectedOrderTab"]) as Tab;
                        if (tab) {
                            tab.scrollIntoView();
                        }
                    }
                });
            }
        });
        orderPanel.slideIn({ speed: 200 });
    }

    override onLoad() {
        this.activeLayout = this.getActiveLayout();
        this.getAdditionalProps();
        this.setupLayouts();
        this.shareNavigateOnClose();

        // If starting on Invoice History (passed via additionalProps), set the orderId and add a listener to load invoice activity
        if (this["additionalProps"]?.["startOnInvoiceHistory"] && this.activeLayout instanceof InvoiceHistoryTabView) {
            const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
            if (urlProps?.key) {
                this.orderId = urlProps.key;
                // Add a listener to load invoice activity after the main data source loads
                this.activeLayout.mainDataSource.addAfterExecutionListener(async () => {
                    if (this.activeLayout instanceof InvoiceHistoryTabView && this.orderId) {
                        await this.activeLayout.sourceOpenItem.search({ order_id_input: this.orderId });
                    }
                });
            }
        }
    }

    /**
     * This method ensures that, when navigation instructions have been provided to one of the layouts (orders,
     * movements, invoice history) and not the other, that those navigation instructions are shared with the other layout.  The result of
     * this is that closing the CrudDecorator from either layout will results in the same behavior (unless different
     * navigation instructions have already been assigned to each layout).
     *
     * Note that in a standard CrudDecorator scenario, only one of the three layouts will have been assigned navigation
     * instructions (whichever layout is the one that is first visible to the user).  Those navigation instructions will
     * be to slide out the CrudDecorator to remove it from the page.  So in most cases we expect those standard slide
     * out instructions to simply be applied the other layouts.
     */
    private shareNavigateOnClose() {
        this.addLayoutLoadListener(() => {
            const orderLayoutHasNavigateOnClose = this.layoutOrders.dataHeader.navigateOnClose != null;
            const movementLayoutHasNavigateOnClose = this.layoutMovementTabView.dataHeader.navigateOnClose != null;
            const invoiceHistoryLayoutHasNavigateOnClose = this.layoutInvoiceHistoryTabView.dataHeader.navigateOnClose != null;
            if ((orderLayoutHasNavigateOnClose || invoiceHistoryLayoutHasNavigateOnClose) && !movementLayoutHasNavigateOnClose)
                this.layoutMovementTabView.dataHeader.navigateOnClose = orderLayoutHasNavigateOnClose ? this.layoutOrders.dataHeader.navigateOnClose : this.layoutInvoiceHistoryTabView.dataHeader.navigateOnClose;
            if ((movementLayoutHasNavigateOnClose || invoiceHistoryLayoutHasNavigateOnClose) && !orderLayoutHasNavigateOnClose)
                this.layoutOrders.dataHeader.navigateOnClose = movementLayoutHasNavigateOnClose ? this.layoutMovementTabView.dataHeader.navigateOnClose : this.layoutInvoiceHistoryTabView.dataHeader.navigateOnClose;
            if ((orderLayoutHasNavigateOnClose || movementLayoutHasNavigateOnClose) && !invoiceHistoryLayoutHasNavigateOnClose)
                this.layoutInvoiceHistoryTabView.dataHeader.navigateOnClose = orderLayoutHasNavigateOnClose ? this.layoutOrders.dataHeader.navigateOnClose : this.layoutMovementTabView.dataHeader.navigateOnClose;
        });
    }

    override getTitleForMode(mode: DataSourceMode): string {
        const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
        return urlProps ? urlProps.orderId ? `Movement - ${urlProps.key}` : `Order - ${urlProps.key}` : "Add Order";
    }

    override get mainDataSource(): DataSource {
        return this.getActiveLayout().mainDataSource;
    }

    override setAllDataSourcesToMode(mode: DataSourceMode): void {
        this.getActiveLayout().setAllDataSourcesToMode(mode);
    }

    override get dataHeader(): DataHeader {
        const dh = this.getActiveLayout().dataHeader;
        if (this.getActiveLayout() == this.layoutOrders) {
            if (dh.layout == null) dh.layout = this.layoutOrders;
            this.copyDataHeaderAttributes(dh, this.layoutMovementTabView.dataHeader);
            this.copyDataHeaderAttributes(dh, this.layoutInvoiceHistoryTabView.dataHeader);
        } else if (this.getActiveLayout() == this.layoutMovementTabView) {
            if (dh.layout == null) dh.layout = this.layoutMovementTabView;
            this.copyDataHeaderAttributes(dh, this.layoutOrders.dataHeader);
            this.copyDataHeaderAttributes(dh, this.layoutInvoiceHistoryTabView.dataHeader);
        } else if (this.getActiveLayout() == this.layoutInvoiceHistoryTabView) {
            if (dh.layout == null) dh.layout = this.layoutInvoiceHistoryTabView;
            this.copyDataHeaderAttributes(dh, this.layoutOrders.dataHeader);
            this.copyDataHeaderAttributes(dh, this.layoutMovementTabView.dataHeader);
        }
        this.updateDataHeader(dh);
        return dh;
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        if (dataHeader.layout != this.activeLayout) {
            dataHeader.layout = this.activeLayout;
        }
        if (this.displayingFromToolbox) {
            dataHeader.showSave = false;
            dataHeader.showSaveAndClose = true;
        }
        this.setDataSourceMode();
    }

    copyDataHeaderAttributes(dh1: DataHeader, dh2: DataHeader) {
        dh2.showClose = dh1.showClose;
        dh2.showSaveAndClose = dh1.showSaveAndClose;
        dh2.onExecute = dh1.onExecute;
    }

    updateDataHeader(dataHeader: DataHeader) {
        dataHeader["labelTitle"].visible = false;
        dataHeader.marginBottom = 0;
        if (dataHeader.dataSource == null) {
            dataHeader.dataSource = this.getActiveLayout().mainDataSource;
        }
    }

    async setupLayouts() {
        this.layoutOrders.displayingFromToolbox = this.displayingFromToolbox;
        this.layoutOrders.enableNextActions = !this.displayingFromToolbox && !this.layoutOrders["create_spot"];
        this.layoutOrders.panelMovementTab.addClickListener(async () => {
            this.handleTabChange(OrderTabViewMode.MOVEMENT, this.layoutOrders);
            if (this.layoutMovementTabView.layoutMovement.layoutAssignedOverview.visible) {
                await this.layoutMovementTabView.layoutMovement.layoutAssignedOverview.sourceMovementOnDisplay();
            }
            if (this.layoutMovementTabView.layoutMovement.layoutAvailableOverview.visible) {
                await this.layoutMovementTabView.layoutMovement.layoutAvailableOverview.sourceMovementOnDisplay();
            }
        });
        this.layoutOrders.panelInvoiceHistoryTab.addClickListener(async () => {
            this.layoutInvoiceHistoryTabView.stopsTable = this.layoutOrders.stopsTable;
            this.handleTabChange(OrderTabViewMode.HISTORY, this.layoutOrders);
        });


        this.layoutMovementTabView.getTitleForMode = () => this.getTitleForMode(this.mode);
        this.layoutMovementTabView.handleOrderTabChange = async () => {
            this.handleTabChange(OrderTabViewMode.ORDER, this.layoutMovementTabView)
            await this.layoutOrders.layoutOverview.sourceOrderOnDisplay();
        };

        this.layoutMovementTabView.handleInvoiceHistoryTabChange = async () => {
            const layoutStopList = this.layoutMovementTabView.layoutMovement.layoutStopList as StopList;
            this.layoutInvoiceHistoryTabView.stopsTable = layoutStopList.tableStop;
            this.handleTabChange(OrderTabViewMode.HISTORY, this.layoutMovementTabView);
        };


        this.layoutMovementTabView.movementTabOnChange = () => this.parent.replace(this.parent.components[0], this.dataHeader);
        this.layoutMovementTabView.movementRefreshed = () => {
            if (this.layoutOrders.activeRow) {
                this.layoutOrders.search({ id: this.layoutOrders.activeRow.get("id") });
            }
        };
        this.layoutMovementTabView.activeMovementChanged = (movement: Movement) => {
            if (movement["doAfterExecuteEvent"] == null) {
                movement["doAfterExecuteEvent"] = (event: DataSourceExecutionEvent) => {
                    this.doAfterExecuteEvent(event, this.layoutOrders);
                    this.doAfterExecuteEvent(event, this.layoutInvoiceHistoryTabView);
                };
                movement.mainDataSource.addAfterExecutionListener(movement["doAfterExecuteEvent"]);
            }
        }
        this.layoutOrders.mainDataSource.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
            this.doAfterExecuteEvent(event, this.layoutMovementTabView);
            this.doAfterExecuteEvent(event, this.layoutInvoiceHistoryTabView);

            if (event.getAction() === DataSourceAction.SEARCH && this.layoutOrders.activeRow) {
                this.layoutOrders.panelMovementTab.enabled = true;
                this.layoutOrders.panelInvoiceHistoryTab.enabled = true;
            }
        });

        this.layoutOrders.carrierAssignMentChanged = () => {
            if (this.layoutMovementTabView.activeRow) {
                this.layoutMovementTabView.layoutMovement.search({ id: this.layoutMovementTabView.activeRow.get("id") });
            }
        }

        this.layoutInvoiceHistoryTabView.handleOrderTabChange = async () => {
            this.handleTabChange(OrderTabViewMode.ORDER, this.layoutInvoiceHistoryTabView)
            await this.layoutOrders.layoutOverview.sourceOrderOnDisplay();
        }

        this.layoutInvoiceHistoryTabView.handleMovementTabChange = async () => {
            this.handleTabChange(OrderTabViewMode.MOVEMENT, this.layoutInvoiceHistoryTabView);
            if (this.layoutMovementTabView.layoutMovement.layoutAssignedOverview.visible) {
                await this.layoutMovementTabView.layoutMovement.layoutAssignedOverview.sourceMovementOnDisplay();
            }
            if (this.layoutMovementTabView.layoutMovement.layoutAvailableOverview.visible) {
                await this.layoutMovementTabView.layoutMovement.layoutAvailableOverview.sourceMovementOnDisplay();
            }
        }

        this.layoutInvoiceHistoryTabView.mainDataSource.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
            this.doAfterExecuteEvent(event, this.layoutMovementTabView);
            this.doAfterExecuteEvent(event, this.layoutOrders);
        });
    }

    setDataSourceMode() {
        const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
        if (this.displayingFromToolbox || urlProps == null || urlProps?.mode == "add" || this.dataHeader.parent["mode"] == DataSourceMode.ADD) {
            this.mode = DataSourceMode.ADD;
            this.layoutOrders.panelMovementTab.visible = false;
            this.layoutOrders.panelInvoiceHistoryTab.visible = false;
            this.layoutOrders.panelOrderTab.borderRightWidth = 0;
            this.layoutOrders.labelOrderView.text = "Add Order";
            this.layoutOrders.labelStatusBadge.visible = false;
        } else {
            this.mode = DataSourceMode.UPDATE;
        }
    }

    getAdditionalProps() {
        let additionalProps = this["additionalProps"];

        // For new tab scenarios, also check URL parameters if additionalProps is not available
        if (!additionalProps) {
            const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
            if (urlProps?.showVoid === "true") {
                additionalProps = { showVoid: true };
            }
        }

        if (additionalProps) {
            for (const prop in additionalProps) {
                if (this.activeLayout instanceof MovementTabView) {
                    this.activeLayout.layoutMovement[prop] = additionalProps[prop];
                    if (prop === "showVoid") {
                        this.activeLayout.showVoid = additionalProps[prop];
                    }
                } else {
                    this.activeLayout[prop] = additionalProps[prop];
                }
            }
        }
    }

    getActiveLayout() {
        let activeLayout: Orders | MovementTabView | InvoiceHistoryTabView = this.activeLayout;
        if (this.activeLayout == null) {
            // Check if we should start on Invoice History tab (passed via additionalProps)
            if (this["additionalProps"]?.["startOnInvoiceHistory"]) {
                activeLayout = this.layoutInvoiceHistoryTabView;
                activeLayout.hideTitle = false;
            } else {
                activeLayout = this.layoutOrders;
                activeLayout.hideTitle = false;
                if (!this.openedFromToolbox) {
                    const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
                    if (urlProps?.orderId) {
                        if (urlProps?.orderId === "none")
                            urlProps.orderId = null;
                        activeLayout = this.layoutMovementTabView;
                    }
                }
            }
        }
        return activeLayout;
    }

    get activeLayout(): Orders | MovementTabView | InvoiceHistoryTabView {
        return this._activeLayout;
    }

    set activeLayout(layout: Orders | MovementTabView | InvoiceHistoryTabView) {
        this._activeLayout = layout;
        this._activeLayout.visible = true;
    }

    async handleTabChange(orderTabViewMode: OrderTabViewMode, layout: Orders | MovementTabView | InvoiceHistoryTabView) {
        if (!this.activeLayout.activeRow)
            return;

        // Check for unsaved changes
        if (this.activeLayout.mainDataSource.hasChanged(true)) {
            const saveChanges = await CommonDialogs.showYesNo("Save your changes on this tab before switching?", "Save Changes");
            if (saveChanges == undefined) {
                return;
            } else if (saveChanges) {
                await this.activeLayout.mainDataSource.post();
            }
        }

        // Set orderId if needed
        if (StringUtil.isEmptyString(this.orderId) &&
            orderTabViewMode === OrderTabViewMode.MOVEMENT &&
            layout instanceof Orders) {
            this.orderId = this.layoutOrders.activeRow.get("id");
        }

        // Update layouts and data header
        const oldDataHeader = this.dataHeader;
        const id = this.setActiveLayoutAndId(orderTabViewMode, layout);
        try {
            await this.searchData(id);
        } catch (e) {
            CommonDialogs.showError("Failed to load data for the selected tab.");
            return;
        }
        this.parent.replace(oldDataHeader, this.dataHeader);
    }

    setActiveLayoutAndId(orderTabViewMode: OrderTabViewMode, layout: Orders | MovementTabView | InvoiceHistoryTabView): string {
        let id: string;

        // Set all layouts to not visible first
        this.setLayoutsToNotVisible();

        // Then set the active layout and determine the appropriate ID
        switch (orderTabViewMode) {
            case OrderTabViewMode.MOVEMENT:
                this.activeLayout = this.layoutMovementTabView;
                id = layout instanceof Orders ?
                    this.layoutOrders.activeRow?.get("curr_movement_id") :
                    this.layoutInvoiceHistoryTabView.activeRow?.get("curr_movement_id");
                break;

            case OrderTabViewMode.ORDER:
                this.activeLayout = this.layoutOrders;
                id = layout instanceof MovementTabView ?
                    this.layoutMovementTabView.activeRow?.get("orders.id") :
                    this.layoutInvoiceHistoryTabView.activeRow?.get("id");
                break;

            case OrderTabViewMode.HISTORY:
                this.activeLayout = this.layoutInvoiceHistoryTabView;
                if (this.orderId) {
                    id = this.orderId;
                } else {
                    id = layout instanceof Orders ?
                        this.layoutOrders.activeRow?.get("id") :
                        this.layoutMovementTabView.activeRow?.get("orders.id");
                }
                break;
        }

        // Make sure we never return undefined/null
        return id || '';
    }

    async searchData(id: string) {
        if (!id) {
            return; // Don't attempt to search with an empty ID
        }

        if (!this.activeLayout.activeRow) {
            await this.activeLayout.search({ id });
            if (this.activeLayout instanceof InvoiceHistoryTabView) {
                await this.activeLayout.sourceOpenItem.search({ order_id_input: id });
            }
            this.activeLayout.mainDataSource.mode = this.mode;
        }
    }

    setLayoutsToNotVisible() {
        this.layoutOrders.visible = false;
        this.layoutMovementTabView.visible = false;
        this.layoutInvoiceHistoryTabView.visible = false;
    }

    async doAfterExecuteEvent(event: DataSourceExecutionEvent, layoutToRefresh: Orders | MovementTabView | InvoiceHistoryTabView) {
        if (event.getAction() == DataSourceAction.UPDATE) {
            if (layoutToRefresh instanceof Orders) {
                // Post moves that have changed data
                const changedMovement = this.layoutMovementTabView.getChangedMovement();
                if (changedMovement != null) {
                    changedMovement.mainDataSource.post();
                    return;
                }
            }
            if (layoutToRefresh.activeRow) {
                if (layoutToRefresh instanceof MovementTabView) {
                    layoutToRefresh.layoutMovementTabs.currentTabId = null;
                }
                layoutToRefresh.search({ id: layoutToRefresh.activeRow.get("id") });
            }
        }

    }

    public static addNavigationContextMenuItems(itemProps: Partial<ContextMenuLabelProps>[], orderId: string, movementId: string = null): Partial<ContextMenuLabelProps>[] {
        if (itemProps != null) {
            const navigationItems = OrderTabView.createNavigationContextMenuItems(orderId, movementId);
            if (itemProps.length > 0 && navigationItems.length > 0) {
                const allVisibleItems = itemProps.filter(item => item.visible);
                const lastVisibleItem = allVisibleItems[allVisibleItems.length - 1];
                if (lastVisibleItem) lastVisibleItem.separatorAfter = true;
                itemProps.push(...navigationItems);
            }
        }
        return itemProps;
    }

    public static createNavigationContextMenuItems(orderId: string, movementId: string = null): Partial<ContextMenuLabelProps>[] {
        if (movementId == null && orderId == null) return [];
        return [
            {
                text: "Open in New Tab",
                onClick: () => OrderTabView.navigateTo({
                    key: movementId ?? orderId,
                    ...(movementId ? { orderId } : {}),
                    newTab: true
                })
            },
            {
                text: "Open in New Window",
                onClick: () => OrderTabView.navigateTo({
                    key: movementId ?? orderId,
                    ...(movementId ? { orderId } : {}),
                    newTab: true,
                    styleProps: { windowDecorators: false }
                })
            }
        ]
    }

    set orderId(orderId: string) {
        this._orderId = orderId;
    }

    get orderId(): string {
        return this._orderId;
    }
}
